import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldOff, Loader2 } from "lucide-react";
import { useAuth } from "../../lib/auth";
import API from "../../lib/api";

/**
 * Phase 7.1 — AdminGuard
 * --------------------------------------------------------------
 * Wraps any /admin/* route. Soft-gates via /api/admin/ping so that
 * both env-allowlisted and DB-allowlisted admins can pass through,
 * without us having to know the user's `is_admin` flag locally.
 *
 * - Loading       → premium spinner placeholder
 * - Anon          → redirects to /login?next=<path>
 * - Non-admin     → premium "Not authorised" panel
 * - Admin         → renders children
 */
export default function AdminGuard({ children }) {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [pingState, setPingState] = useState(null); // null = not yet checked; {isAdmin, source} once done

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      // No user → no ping needed; we'll redirect below.
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.get("/admin/ping");
        if (!cancelled) setPingState({ isAdmin: !!data?.is_admin, source: data?.source || null });
      } catch {
        if (!cancelled) setPingState({ isAdmin: false, source: null });
      }
    })();
    return () => { cancelled = true; };
  }, [user, authLoading]);

  // Loading: still resolving auth, OR we have a user but haven't pinged yet
  if (authLoading || (user && pingState === null)) {
    return (
      <div className="min-h-[60vh] grid place-items-center" data-testid="admin-guard-loading">
        <div className="flex items-center gap-3 text-white/70">
          <Loader2 className="w-5 h-5 animate-spin gold-text" />
          <span className="text-sm">{t("admin.shell.checking", "Checking admin access\u2026")}</span>
        </div>
      </div>
    );
  }

  // Anonymous → redirect to login
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  // Authenticated but not admin → premium block
  if (!pingState || !pingState.isAdmin) {
    return (
      <div className="min-h-[70vh] grid place-items-center px-4" data-testid="admin-guard-blocked">
        <div className="card-surface p-8 md:p-10 max-w-md w-full text-center">
          <div className="w-12 h-12 mx-auto rounded-full bg-gradient-to-br from-[#E2C77A]/30 to-[#7A5F2A]/15 border border-[#C8A24A]/40 grid place-items-center mb-4">
            <ShieldOff className="w-5 h-5 gold-text" />
          </div>
          <div className="eyebrow mb-2">{t("admin.guard.eyebrow", "\u2014 Restricted")}</div>
          <h2 className="font-display text-2xl mb-2" data-testid="admin-guard-title">
            {t("admin.guard.title", "Admin access required")}
          </h2>
          <p className="text-sm text-white/70 mb-6 leading-relaxed">
            {t("admin.guard.subtitle", "You don\u2019t have permission to view this page.")}
          </p>
          <a href="/" className="btn-secondary inline-block text-sm" data-testid="admin-guard-home-btn">
            {t("admin.guard.go_home", "Go home")}
          </a>
        </div>
      </div>
    );
  }

  return children;
}
